import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  Collapse,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap'
import Slider from 'rc-slider'
import get from 'lodash.get'
import debounce from 'lodash.debounce'
import {
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import { FormattedMessage } from 'react-intl'
import compact from 'lodash.compact'
import includes from 'lodash.includes'
import map from 'lodash.map'

import { FaSort, FaEllipsisV, FaEye, FaEyeSlash, FaTrash, FaAdjust, FaDownload, FaDatabase } from 'react-icons/fa';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

const CollapseMetadata = styled(Collapse)`
  font-style: italic;
  max-height: 100px;
  overflow: auto;
`

const ItemLabel = styled.div`
  flex-grow: 1;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 24px;
  color: #444;
`

const ItemAction = styled.div`
  cursor: row-resize;

  svg {
    display: inline-block;
    vertical-align: middle;
  }
`

const LayerBoxItemContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  user-select: none;
  background: #f6f6f6;
  padding-top: 5px;
  padding-bottom: 10px;
  border-bottom: 1px solid #cacaca;

  &:last-child {
    border-bottom: none;
  }
`

const LayerBoxItemController = styled.div`
  display: flex;
`

const DropDownToggleButton = styled(DropdownToggle)`
  &&& {
    background-color: transparent;
    border: none;
    color: #000;
    outline: none;
    padding: 0 2px;
  }
`

const DropdownItemStyled = styled(DropdownItem)`
  &&& {
    svg,
    span {
      display: inline-block;
      vertical-align: middle;
    }

    svg {
      margin-right: 8px;
    }
  }
`

const LegendList = styled.ul`
  max-height: 308px;
  padding: 0 0 10px 10px;
  margin: 0;
  list-style: none;
  overflow-y: scroll;
`

const LegendListItemColor = styled.span`
  display: inline-block;
  width: 24px;
  height: 10px;
  margin-right: 8px;
  vertical-align: middle;
  border-radius: 2px;
  background-position: center center;
  background-size: contain;
`

const LegendListItemLabel = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-size: 12px;
`

const LegendContainer = styled.div`
  margin-top: 4px;
  background: #efefef;
  border-radius: 4px;
  overflow: hidden;
`

const CollapseButton = styled.button`
  width: 100%;
  padding: 4px 4px 4px 8px;
  text-align: left;
  color: #333;
  background: #eaeaea;
  border: none;
  cursor: pointer;
  transition: background 200ms ease-in-out;

  &:hover {
    background: #dedede;
  }
`

const CollapseButtonIcon = styled.span`
  float: right;
  font-size: 20px;
  color: #555;
  line-height: 14px;
`

const VisibilityButtonToggle = styled.button`
  padding: 0 4px;
  background: none;
  border: none;
  cursor: default;
`

const DragHandle = SortableHandle(() => {
  return (
    <ItemAction className='mr-1'>
      <FaSort />
    </ItemAction>
  )
})

class LayerBoxItem extends PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      dropDownOptionsIsOpen: false,
      metadataIsOpen: false,
      opacityIsOpen: false,
      legendIsVisible: props.isFirstItem
    }

    this.onChangeOpacity = this.onChangeOpacity.bind(this)
    this.onDeleteLayer = this.onDeleteLayer.bind(this)
    this.onToggleLayer = this.onToggleLayer.bind(this)
    this.onToggleDropDownOptions = this.onToggleDropDownOptions.bind(this)
    this.onToggleMetadata = this.onToggleMetadata.bind(this)
    this.onToggleOpacity = this.onToggleOpacity.bind(this)
  }

  onChangeOpacity (value) {
    const { editLayer, layer } = this.props

    editLayer(layer.id, 'opacity', value)
  }

  onDeleteLayer () {
    const { deleteLayer, layer } = this.props

    this.setState({
      dropDownOptionsIsOpen: false,
      metadataIsOpen: false,
      opacityIsOpen: false
    }, () => deleteLayer(layer.id))
  }

  onToggleDropDownOptions () {
    this.setState({
      dropDownOptionsIsOpen: !this.state.dropDownOptionsIsOpen
    })
  }

  onToggleMetadata () {
    this.props.onMetadataClick(this.props.layerData);
  }

  onToggleOpacity () {
    this.setState({
      opacityIsOpen: !this.state.opacityIsOpen
    })
  }

  onToggleLegendVisibility () {
    this.setState({
      legendIsVisible: !this.state.legendIsVisible
    })
  }

  onToggleLayer () {
    const { editLayer, layer } = this.props

    const visibility = get(layer, 'config.visibility', true)

    editLayer(layer.id, 'visibility', !visibility)
  }

  renderLegend () {
    const { layerData, legendData } = this.props
    let parsedLegend = map(legendData, (value, key) => {
      if (includes(key, 'color')) { return null; }
      return { label: value, color: legendData[`${ key }.color`] };
    })
    parsedLegend = compact(parsedLegend)
    const isOpen = this.state.legendIsVisible;

    return (
      <LegendContainer>
        <CollapseButton onClick={this.onToggleLegendVisibility.bind(this)}>
          <FormattedMessage id="layer.opt.legend" />
          <CollapseButtonIcon>
            { isOpen ? <MdExpandLess /> : <MdExpandMore /> }
          </CollapseButtonIcon>
        </CollapseButton>
        <Collapse isOpen={ isOpen }>
          <LegendList>
            { map(parsedLegend, ({ color, label }, index) => {
              const colorStyles = !color.startsWith('http') ? { backgroundColor: color } : { backgroundImage: `url(${ color })` , backgroundRepeat : 'no-repeat' };
              return (
                <li key={ `legend-${ layerData.id_layer }-${ index }` }>
                  <LegendListItemColor style={ colorStyles } />
                  <LegendListItemLabel>{ label }</LegendListItemLabel>
                </li>
              );
            }) }
          </LegendList>
        </Collapse>
      </LegendContainer>
    )
  }

  renderMetadata () {
    const { layerData } = this.props
    const { metadataIsOpen } = this.state

    return (
      <CollapseMetadata className='text-center w-100' isOpen={metadataIsOpen}>
        <FormattedMessage id={ `metadata.layer.${ layerData.id_layer }` } />
      </CollapseMetadata>
    )
  }

  renderOpacityController () {
    const { opacityIsOpen } = this.state
    const opacityValue = get(this.props.layer, 'config.opacity', 100)

    if (!opacityIsOpen) {
      return null
    }

    return (
      <LayerBoxItemController className='w-100'>
        <Collapse className='d-flex justify-content-center w-100' isOpen={opacityIsOpen}>
          <Slider
            className='w-75'
            onChange={debounce(this.onChangeOpacity, 500)}
            step={10}
            defaultValue={opacityValue} />
        </Collapse>
      </LayerBoxItemController>
    )
  }

  render () {
    const { index, layer, layerData } = this.props
    const { dropDownOptionsIsOpen } = this.state
    const isVisible = get(layer, 'config.visibility', true)

    const SortableItem = SortableElement(() => {
      return (
        <LayerBoxItemContainer>
          <LayerBoxItemController className='w-100'>
            <DragHandle />
            <ItemLabel className='mx-1'>
              <FormattedMessage id={ `name.layer.${ layerData.id_layer }` } />
            </ItemLabel>
            { !isVisible &&
              <VisibilityButtonToggle>
                <FaEyeSlash />
              </VisibilityButtonToggle>
            }
            <Dropdown isOpen={dropDownOptionsIsOpen} toggle={this.onToggleDropDownOptions}>
              <DropDownToggleButton>
                <FaEllipsisV />
              </DropDownToggleButton>
              <DropdownMenu>
                <DropdownItemStyled onClick={this.onToggleMetadata}>
                  <FaDatabase /> <FormattedMessage id="layer.opt.metadata" />
                </DropdownItemStyled>
                <DropdownItemStyled onClick={this.onToggleOpacity}>
                  <FaAdjust /> <FormattedMessage id="layer.opt.opacity" />
                </DropdownItemStyled>
                <DropdownItemStyled href={ layerData.metadata.download } target="_blank">
                  <FaDownload /> <FormattedMessage id="layer.opt.download" />
                </DropdownItemStyled>
                <DropdownItemStyled onClick={this.onToggleLayer}>
                  <FaEye /> <FormattedMessage id="layer.opt.hide" />
                </DropdownItemStyled>
                <DropdownItemStyled onClick={this.onDeleteLayer}>
                  <FaTrash /> <FormattedMessage id="layer.opt.remove" />
                </DropdownItemStyled>
              </DropdownMenu>
            </Dropdown>
          </LayerBoxItemController>
          {this.renderOpacityController()}
          {this.renderMetadata()}
          {this.renderLegend()}
        </LayerBoxItemContainer>
      )
    })

    return <SortableItem index={index} />
  }
}

LayerBoxItem.propTypes = {
  editLayer: PropTypes.func.isRequired,
  deleteLayer: PropTypes.func.isRequired,
  layer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    config: PropTypes.object
  }).isRequired,
  sortLayers: PropTypes.func.isRequired
}

export default LayerBoxItem



// WEBPACK FOOTER //
// ./src/components/atoms/LayerBoxItem/LayerBoxItem.js